import { useQuery } from '@apollo/client';
import { ME } from '~/graphql/queries';
import type { MeQuery } from '~/graphql/gen/graphql';

export interface PersonData {
  personId: number;
  firstName: string | null;
  lastName: string | null;
}

export interface UseOnboardingDataResult {
  personData: PersonData | null;
  loading: boolean;
  error?: Error;
}

/**
 * Custom hook to fetch user's person record from the ME query.
 * Finds the person where userId matches the logged-in user's ID.
 * Returns personId, firstName, and lastName for form logic.
 */
export const useOnboardingData = (): UseOnboardingDataResult => {
  const { data, loading, error } = useQuery<MeQuery>(ME);

  if (loading) {
    return {
      personData: null,
      loading: true,
    };
  }

  if (error) {
    return {
      personData: null,
      loading: false,
      error,
    };
  }

  if (!data?.me?.ownedTree?.persons) {
    return {
      personData: null,
      loading: false,
    };
  }

  // Find the person record where userId matches me.id
  const userPerson = data.me.ownedTree.persons.find(
    (person) => person?.userId === data.me?.id,
  );

  if (!userPerson) {
    return {
      personData: null,
      loading: false,
    };
  }

  return {
    personData: {
      personId: userPerson.id,
      firstName: userPerson.firstName ?? null,
      lastName: userPerson.lastName ?? null,
    },
    loading: false,
  };
};
